import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'

// Phase 2: flat list of the signed-in user's current responses across all
// initiatives. v1 doesn't surface intersections here — that's Phase 3.

interface ResponseRow {
  id: number
  modality: string
  status: string
  raw_input: string | null
  structured_response: string | null
  received_at: string
  superseded_at: string | null
  prompt_activation_id: number | null
  prompt: {
    id: number
    question_text: string
  } | null
  initiative: {
    id: number
    slug: string
    name: string
  } | null
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  transcribed: 'Transcribed',
  transcription_failed: 'Transcription failed',
  analyzed: 'Analyzed',
  analysis_failed: 'Analysis failed',
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-700',
  transcribed: 'bg-amber-50 text-amber-700',
  transcription_failed: 'bg-rose-50 text-rose-700',
  analyzed: 'bg-emerald-50 text-emerald-700',
  analysis_failed: 'bg-rose-50 text-rose-700',
}

export default function MyResponses() {
  const [rows, setRows] = useState<ResponseRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    api
      .get('/api/v1/me/responses')
      .then((data: ResponseRow[]) => {
        if (!cancelled) setRows(data)
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">My responses</h2>
        <p className="mt-1 text-sm text-slate-500">
          Everything you've submitted across your initiatives.
        </p>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {error && !loading && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-500">
          You haven't submitted any responses yet.{' '}
          <Link to="/" className="text-accent-600 hover:underline">
            Start on Home →
          </Link>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li
              key={row.id}
              className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {row.initiative?.name || 'Unknown initiative'}
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-900 line-clamp-2">
                    {row.prompt?.question_text || '(prompt removed)'}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={[
                      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                      STATUS_COLORS[row.status] || 'bg-slate-100 text-slate-700',
                    ].join(' ')}
                  >
                    {STATUS_LABELS[row.status] || row.status}
                  </span>
                  <span className="text-xs text-slate-500">
                    {formatReceivedAt(row.received_at)}
                  </span>
                </div>
              </div>

              {row.raw_input && (
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap line-clamp-3">
                  {row.raw_input}
                </p>
              )}

              {row.initiative && (
                <div className="mt-3">
                  <Link
                    to={
                      row.prompt_activation_id
                        ? `/initiatives/${row.initiative.slug}/respond?activation=${row.prompt_activation_id}`
                        : `/initiatives/${row.initiative.slug}/respond`
                    }
                    className="text-xs text-accent-600 hover:underline"
                  >
                    Open in Respond →
                  </Link>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function formatReceivedAt(iso: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
